import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Cpu, Database, Settings, Package, Sparkles, ArrowRight } from 'lucide-react';
import { cn } from "@/lib/utils";
import SEO, { getServiceSchema, getBreadcrumbSchema } from '../components/SEO';

const DigitalPersonaDevelopment = () => {
  const [activeCategory, setActiveCategory] = useState('standard');
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const services = [
    {
      icon: Database,
      title: "Precision Dataset Engineering",
      description: "Every great model starts with exceptional data. We curate, crop, and optimize your images for maximum training impact — no generic datasets, no compromises."
    },
    {
      icon: Cpu,
      title: "Private SDXL LoRA Training",
      description: "Your likeness, trained on dedicated GPU infrastructure. We engineer custom LoRA models that capture your unique features with remarkable fidelity."
    },
    {
      icon: Settings,
      title: "Trigger Word & Output Calibration",
      description: "Fine-tuned activation and style control. We calibrate your model's response to deliver predictable, professional-grade results every time."
    },
    {
      icon: Package,
      title: "Commercial-Ready Delivery",
      description: "Receive production-ready model files, usage documentation, and recommended prompt frameworks — everything you need to generate at scale."
    }
  ];

  const standardPackages = [
    {
      name: "Starter Expansion",
      price: "$125",
      description: "Explore your visual identity with curated variations before committing to full model training.",
      features: [
        "20 curated character variations",
        "Controlled angle & lighting diversity",
        "Dataset-ready formatting",
        "Prompt documentation included",
        "Personal use license"
      ],
      featured: false,
      nudge: "Ready for a trained model? See Private LoRA Build →"
    },
    {
      name: "Private LoRA Build",
      price: "$350",
      description: "Own a professionally trained private model with full creative control and scalable output.",
      features: [
        "40–60 precision-curated images",
        "Custom SDXL LoRA training",
        "Trigger-word activation setup",
        "Output calibration & realism tuning",
        "Private model delivery",
        "Personal + limited commercial license"
      ],
      featured: true,
      nudge: "Need full commercial rights? Explore Full Ownership →"
    },
    {
      name: "Full Ownership Package",
      price: "$750+",
      description: "Acquire a commercial-grade AI identity system engineered for long-term brand deployment.",
      features: [
        "60–100 expanded curated dataset",
        "Advanced SDXL LoRA with precision calibration",
        "Multi-angle realism balancing",
        "Output consistency stress testing",
        "Controlled style flexibility",
        "Private delivery — never shared, never public",
        "Full IP transfer & commercial rights"
      ],
      featured: false,
      elite: true,
      nudge: "The complete system. Nothing held back."
    }
  ];

  const creatorPackages = [
    {
      name: "Creator Starter",
      price: "$500",
      description: "Establish your private digital double using your real likeness and identity.",
      features: [
        "40 curated dataset images based on your real appearance",
        "Private SDXL LoRA training from your likeness",
        "Trigger-word engineering and activation calibration",
        "Identity consistency optimization",
        "Private model delivery",
        "Licensed for subscription platform content creation"
      ],
      featured: false,
      nudge: "Ideal for creators beginning to scale content without the need for daily physical preparation."
    },
    {
      name: "Creator Professional",
      price: "$1,200",
      description: "A fully refined digital double engineered for high-volume subscription content production.",
      features: [
        "60–100 curated dataset images based on your real likeness and body",
        "Advanced SDXL LoRA training with identity locking",
        "Multi-angle and expression consistency calibration",
        "Controlled aesthetic flexibility while preserving identity",
        "Output optimization for consistent subscription content production",
        "Private model delivery",
        "Full commercial use for subscription platforms"
      ],
      featured: true,
      nudge: "Designed for creators who want to significantly reduce time spent on makeup, wardrobe, and staging while maintaining full visual authenticity."
    },
    {
      name: "Creator Elite Digital Double",
      price: "$2,500+",
      description: "A complete digital identity system engineered for maximum realism, control, and long-term scalability.",
      features: [
        "Expanded dataset engineering (100+ images from your real likeness)",
        "Elite-tier SDXL LoRA training with advanced identity preservation",
        "Full consistency and realism stress testing",
        "Advanced trigger engineering and output control",
        "Dedicated refinement and optimization session",
        "Private model delivery — never shared, never public",
        "Full ownership and unrestricted commercial rights"
      ],
      featured: false,
      elite: true,
      nudge: "This package creates a permanent digital asset built directly from your real identity — enabling scalable content production without daily physical preparation."
    }
  ];

  const monthlyAddOn = {
    name: "Monthly Generation Access",
    price: "$200",
    period: "/ month",
    description: "For creators who want continuous, hands-off access to professional image generation using their private digital double.",
    features: [
      "Direct generation access using your private trained model",
      "Professionally generated content delivered monthly",
      "Consistent identity preservation across all outputs",
      "Ongoing optimization and quality control",
      "Priority generation queue"
    ],
    note: "This allows you to generate new content on demand without managing training, prompts, or technical workflows yourself."
  };

  const processSteps = [
    { num: "01", title: "Discovery", description: "Share your goals, preferred aesthetic, and intended use cases. We'll recommend the right package for your needs." },
    { num: "02", title: "Image Curation", description: "Submit your source images. We'll guide you on what works best — or provide a shot list for optimal results." },
    { num: "03", title: "Dataset Engineering", description: "Your images are processed, cropped, and optimized for training. Quality in, quality out." },
    { num: "04", title: "Model Training", description: "We run dedicated GPU training sessions, testing checkpoints until your model achieves consistent, accurate output." },
    { num: "05", title: "Delivery & Support", description: "Receive your model files, documentation, and prompt recommendations. We're here if you need refinements." }
  ];

  const perfectFor = [
    "Content Creators & Influencers",
    "Professional Speakers & Coaches",
    "Entrepreneurs Building Personal Brands",
    "Authors & Course Creators",
    "Real Estate Professionals",
    "Marketing Teams & Agencies"
  ];

  const currentPackages = activeCategory === 'standard' ? standardPackages : creatorPackages;

  // Check scroll state
  const checkScrollState = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollState();
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollState);
      return () => carousel.removeEventListener('scroll', checkScrollState);
    }
  }, [activeCategory]);

  // Reset scroll position when category changes
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [activeCategory]);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.querySelector('.package-card')?.offsetWidth || 380;
      const gap = 24;
      const scrollAmount = direction === 'left' ? -(cardWidth + gap) : (cardWidth + gap);
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        scrollCarousel('left');
      } else if (e.key === 'ArrowRight') {
        scrollCarousel('right');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1724]">
      {/* SEO Meta Tags - Nationwide Service */}
      <SEO
        title="Digital Persona Development | AI LoRA Model Training"
        description="Create your private AI model with custom SDXL LoRA training. Full ownership, commercial rights, unlimited generations. Professional AI persona development nationwide."
        path="/services/digital-persona-development"
        jsonLd={[
          getServiceSchema({
            name: 'Digital Persona Development',
            description: 'Private AI model engineering and custom SDXL LoRA training for creators, influencers, and brands. Full ownership with commercial rights.',
            url: 'https://rcstudios.net/services/digital-persona-development',
            areaServed: 'United States',
            priceRange: '$125-$750'
          }),
          getBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: 'Digital Persona Development', path: '/services/digital-persona-development' }
          ])
        ]}
      />

      {/* Hero Section - Image with Left-Aligned Copy Overlay */}
      <section className="hero-main relative h-[600px] md:h-[650px] lg:h-[700px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_e3beda66-3777-486e-a151-323160967d26/artifacts/2nloth99_Hero-digital-persona.jpg"
            alt="Digital Persona Development - Private AI Model Engineering"
            className="w-full h-full object-cover object-center md:object-top"
          />
          {/* Subtle gradient overlay for text readability on left side */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050816]/80 via-[#050816]/40 to-transparent"></div>
          {/* Top gradient for navbar contrast */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#050816]/60 to-transparent"></div>
        </div>
        
        {/* Hero Content - Left Aligned in Negative Space */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl pt-20 md:pt-24">
              {/* Eyebrow Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C6A14A]/10 border border-[#C6A14A]/20 mb-6">
                <Sparkles className="w-4 h-4 text-[#C6A14A]" />
                <span className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase">Private AI Model Engineering</span>
              </div>
              
              {/* H1 Headline */}
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight">
                Your Face. Your Model.<br />Your Control.
              </h1>
              
              {/* Gold Subhead */}
              <p className="text-[#C6A14A] text-base sm:text-lg md:text-xl font-medium mb-4">
                Private AI Model Engineering for Creators Who Demand More
              </p>
              
              {/* Body Paragraph */}
              <p className="text-white/60 text-sm sm:text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                We build custom SDXL LoRA models trained exclusively on your likeness — delivering consistent, studio-quality outputs you own forever.
              </p>
              
              {/* CTA Button */}
              <Link 
                to="/order/lora-training"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#C6A14A] text-[#0f1724] font-semibold hover:bg-[#C6A14A]/90 transition-all shadow-lg"
                data-testid="hero-commission-btn"
              >
                Commission Your Private Model
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 md:py-6">
          <div className="flex items-center gap-3">
            <Link 
              to="/services" 
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#C6A14A] hover:text-[#C6A14A]/80 transition-colors"
            >
              Services
            </Link>
            <span className="text-[#050816]/30 text-xs">/</span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#050816]">
              Digital Persona Development
            </span>
          </div>
          <div className="w-12 h-[1.5px] bg-[#C6A14A]/40 mt-4"></div>
        </div>
      </section>

      {/* ELITE CHARACTER ENGINEERING SHOWCASE */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-[#0f1724] to-[#141c2b]">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* TOP: Centered Eyebrow + Editorial Headline */}
          <div className="text-center mb-16">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              Elite Character Engineering
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-4 leading-tight">
              Engineered for Consistency.<br className="hidden md:block" /> Designed for Influence.
            </h2>
            <p className="text-[#C6A14A]/70 text-sm tracking-[0.15em] uppercase">
              $750+ Consistent Character Influencer Tier
            </p>
          </div>

          {/* MIDDLE: Two-column layout */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-20">
            
            {/* Left: High-detail close-up portrait */}
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div 
                  className="absolute -inset-4 rounded-3xl opacity-20 pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse at center, rgba(198, 161, 74, 0.3) 0%, transparent 70%)' }}
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_e3beda66-3777-486e-a151-323160967d26/artifacts/iknv58gq_openart-ac05772c26b84197ac1af9d3b8dfd7a5_raw.png"
                  alt="High-detail digital persona - close-up portrait showcasing extreme realism"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                  style={{ boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.5)' }}
                />
              </div>
            </div>

            {/* Right: Luxury description + bullet breakdown */}
            <div className="order-1 lg:order-2">
              <div className="space-y-6 text-white/70 leading-relaxed mb-10">
                <p className="text-white text-lg font-medium">
                  This is not a template avatar.
                </p>
                <p>
                  This is a precision-trained digital identity built for long-term brand ownership, visual consistency, and commercial deployment.
                </p>
                <p>
                  Every high-tier persona is developed through advanced dataset curation, controlled LoRA training, trigger-word calibration, and output stabilization across environments, lighting conditions, and camera perspectives.
                </p>
                <p>
                  From skin texture realism and natural micro-expressions to anatomical accuracy and fabric physics — every variable is refined for believable continuity.
                </p>
              </div>

              {/* The Result Block */}
              <div className="border-l-2 border-[#C6A14A]/30 pl-6 mb-10">
                <p className="text-white/90 font-medium mb-3">The result:</p>
                <div className="space-y-1.5 text-white/60">
                  <p>A character that behaves consistently.</p>
                  <p>Photographs convincingly.</p>
                  <p>Scales commercially.</p>
                  <p className="text-white/80 italic">And feels human — not synthetic.</p>
                </div>
              </div>

              {/* Technical Detail Bullet List */}
              <div className="space-y-3">
                {[
                  "High-resolution dataset curation (lighting, angles, expressions)",
                  "Controlled SDXL LoRA architecture tuning",
                  "Identity lock consistency across poses & environments",
                  "Micro-detail calibration (skin, eyes, hair, texture realism)",
                  "Prompt framework engineering for reproducible outputs",
                  "Commercial-ready deployment files & usage systems"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C6A14A] mt-2.5 flex-shrink-0" />
                    <span className="text-white/60 text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* BOTTOM: 3 Refinement Pillars */}
          <div className="grid md:grid-cols-3 gap-8 pt-10 border-t border-white/5">
            {[
              {
                title: "Identity Stability",
                description: "Predictable likeness retention across scenes and styles."
              },
              {
                title: "Visual Realism",
                description: "Natural lighting response, believable anatomy, and fine detail fidelity."
              },
              {
                title: "Commercial Scalability",
                description: "Designed for branding, influencer deployment, content pipelines."
              }
            ].map((pillar, idx) => (
              <div key={idx} className="text-center md:text-left">
                <h3 className="text-white font-semibold text-lg mb-2">{pillar.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES CAROUSEL SECTION */}
      <section id="packages" className="py-16 md:py-24 bg-[#0f1724]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Investment Tiers
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Packages
            </h2>
            <p className="text-white/50 text-lg">
              Select the level of control that matches your ambition.
            </p>
          </div>

          {/* Segmented Toggle */}
          <div className="flex justify-center mb-10" role="tablist" aria-label="Package category">
            <div className="inline-flex p-1 bg-[#141c2b] rounded-xl border border-white/10">
              <button
                onClick={() => setActiveCategory('standard')}
                role="tab"
                aria-selected={activeCategory === 'standard'}
                aria-controls="standard-packages"
                className={cn(
                  "px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300",
                  activeCategory === 'standard'
                    ? "bg-[#C6A14A] text-[#0f1724]"
                    : "text-white/60 hover:text-white"
                )}
              >
                Standard Persona Builds
              </button>
              <button
                onClick={() => setActiveCategory('creator')}
                role="tab"
                aria-selected={activeCategory === 'creator'}
                aria-controls="creator-packages"
                className={cn(
                  "px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300",
                  activeCategory === 'creator'
                    ? "bg-[#C6A14A] text-[#0f1724]"
                    : "text-white/60 hover:text-white"
                )}
              >
                Creator Digital Doubles
              </button>
            </div>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Previous Button */}
            <button
              onClick={() => scrollCarousel('left')}
              disabled={!canScrollLeft}
              aria-label="Previous package"
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                "bg-[#141c2b] border border-white/10 shadow-lg",
                canScrollLeft 
                  ? "text-white hover:border-[#C6A14A]/50 hover:text-[#C6A14A]" 
                  : "text-white/20 cursor-not-allowed",
                "hidden md:flex -translate-x-1/2 lg:-translate-x-full lg:left-2"
              )}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Next Button */}
            <button
              onClick={() => scrollCarousel('right')}
              disabled={!canScrollRight}
              aria-label="Next package"
              className={cn(
                "absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                "bg-[#141c2b] border border-white/10 shadow-lg",
                canScrollRight 
                  ? "text-white hover:border-[#C6A14A]/50 hover:text-[#C6A14A]" 
                  : "text-white/20 cursor-not-allowed",
                "hidden md:flex translate-x-1/2 lg:translate-x-full lg:right-2"
              )}
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Carousel */}
            <div
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-1"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {currentPackages.map((pkg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "package-card flex-shrink-0 snap-center",
                    "w-[340px] sm:w-[380px]",
                    "rounded-2xl border transition-all duration-300 overflow-hidden",
                    pkg.featured 
                      ? "border-[#C6A14A] bg-[#141c2b] shadow-xl shadow-[#C6A14A]/10" 
                      : pkg.elite
                        ? "border-[#C6A14A]/30 bg-gradient-to-b from-[#141c2b] to-[#0f1724]"
                        : "border-white/10 bg-[#141c2b]"
                  )}
                >
                  {/* Badge */}
                  {pkg.featured && (
                    <div className="bg-[#C6A14A] text-[#0f1724] text-xs font-semibold tracking-[0.2em] uppercase text-center py-2">
                      Most Popular
                    </div>
                  )}
                  {pkg.elite && (
                    <div className="bg-[#C6A14A]/20 text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase text-center py-2">
                      Elite
                    </div>
                  )}
                  
                  <div className="p-6 flex flex-col min-h-[520px]">
                    <h3 className="font-serif text-2xl text-white mb-1">{pkg.name}</h3>
                    <p className="text-[#C6A14A] text-3xl font-bold mb-3">{pkg.price}</p>
                    <p className="text-white/50 text-sm mb-6">{pkg.description}</p>
                    
                    <ul className="space-y-2.5 mb-6 flex-grow">
                      {pkg.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-[#C6A14A] mt-0.5 flex-shrink-0" />
                          <span className="text-white/70 text-sm leading-snug">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Link 
                      to="/order/lora-training"
                      className={cn(
                        "block text-center py-3 rounded-lg font-semibold transition-all",
                        pkg.featured || pkg.elite
                          ? "bg-[#C6A14A] text-[#0f1724] hover:bg-[#C6A14A]/90"
                          : "bg-white/10 text-white hover:bg-white/20"
                      )}
                    >
                      Start Your Build
                    </Link>
                    
                    {pkg.nudge && (
                      <p className={cn(
                        "text-xs mt-4 pt-4 border-t border-white/10 leading-relaxed text-center",
                        pkg.elite ? "text-[#C6A14A]/70 italic" : "text-white/40"
                      )}>
                        {pkg.nudge}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile Navigation Dots */}
            <div className="flex justify-center gap-2 mt-6 md:hidden">
              {currentPackages.map((_, idx) => (
                <button
                  key={idx}
                  aria-label={`Go to package ${idx + 1}`}
                  onClick={() => {
                    if (carouselRef.current) {
                      const card = carouselRef.current.querySelector('.package-card');
                      if (card) {
                        const cardWidth = card.offsetWidth + 24;
                        carouselRef.current.scrollTo({ left: cardWidth * idx, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="w-2 h-2 rounded-full bg-white/20 hover:bg-[#C6A14A]/50 transition-colors"
                />
              ))}
            </div>
          </div>

          {/* Monthly Add-On (Creator Only) */}
          <div 
            className={cn(
              "transition-all duration-300 ease-in-out overflow-hidden",
              activeCategory === 'creator' 
                ? "max-h-[500px] opacity-100 mt-10" 
                : "max-h-0 opacity-0 mt-0"
            )}
          >
            <div className="max-w-2xl mx-auto">
              <div className="p-6 bg-[#141c2b] rounded-2xl border border-[#C6A14A]/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div>
                    <span className="text-[#C6A14A] text-xs font-semibold tracking-[0.2em] uppercase">Optional Add-On</span>
                    <h3 className="font-serif text-lg text-white mt-1">{monthlyAddOn.name}</h3>
                  </div>
                  <div className="sm:text-right">
                    <span className="text-[#C6A14A] text-xl font-bold">{monthlyAddOn.price}</span>
                    <span className="text-white/40 text-sm">{monthlyAddOn.period}</span>
                  </div>
                </div>
                
                <p className="text-white/50 text-sm mb-4 leading-relaxed">
                  {monthlyAddOn.description}
                </p>
                
                <div className="grid sm:grid-cols-2 gap-2">
                  {monthlyAddOn.features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-[#C6A14A] mt-0.5 flex-shrink-0" />
                      <span className="text-white/60 text-xs">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* How Creator Digital Doubles Work (Creator Only) */}
          <div 
            className={cn(
              "transition-all duration-300 ease-in-out overflow-hidden",
              activeCategory === 'creator' 
                ? "max-h-[2000px] opacity-100 mt-16" 
                : "max-h-0 opacity-0 mt-0"
            )}
            aria-hidden={activeCategory !== 'creator'}
          >
            <div className="max-w-6xl mx-auto">
              {/* Gold divider line */}
              <div className="w-16 h-px bg-[#C6A14A]/40 mx-auto mb-10" />
              
              {/* Header */}
              <div className="text-center mb-12">
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                  How Creator Digital Doubles Work
                </h3>
                <p className="text-[#C6A14A]/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                  Your private AI twin is trained directly from your real likeness, preserving identity, proportions, and visual authenticity.
                </p>
              </div>

              {/* Two-Column Layout */}
              <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                
                {/* Left Column: Text Content */}
                <div className="order-2 lg:order-1">
                  <div className="space-y-6 text-white/60 leading-relaxed mb-10">
                    <p>
                      After completing your order, you'll receive access to a private upload portal along with a structured reference guide. This guide shows the exact angles, lighting, and poses required to ensure accurate training.
                    </p>
                    <p>
                      You'll upload clear reference photos of yourself following the guide. These images are used exclusively to train your private model. For subscription-based creators producing premium or NSFW content, your dataset may include those images as well—so your AI twin remains consistent across the content you actually produce.
                    </p>
                    <p>
                      Once submitted, we engineer your private SDXL LoRA model using precision dataset calibration and identity locking. The result is a controlled digital double that preserves your likeness while enabling scalable content generation without daily preparation, wardrobe setup, or physical staging.
                    </p>
                    <p className="text-white/70 font-medium">
                      Your model is delivered privately and is never shared, published, or reused.
                    </p>
                  </div>

                  {/* Mini-bullets */}
                  <div className="grid sm:grid-cols-2 gap-4 mb-10">
                    {[
                      "Receive reference guide + upload access",
                      "Upload your required image set",
                      "We train and calibrate your private SDXL LoRA",
                      "Private delivery + usage guidance"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C6A14A] flex-shrink-0" />
                        <span className="text-white/50 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div>
                    <Link 
                      to="/order/lora-training"
                      aria-label="Start Creator Build - Begin your digital double commission"
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#C6A14A] text-[#0f1724] font-semibold hover:bg-[#C6A14A]/90 transition-all shadow-lg"
                    >
                      Start Creator Build
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>

                {/* Right Column: Full-body Image */}
                <div className="order-1 lg:order-2">
                  <div className="relative">
                    <div 
                      className="absolute -inset-4 rounded-3xl opacity-15 pointer-events-none"
                      style={{ background: 'radial-gradient(ellipse at center, rgba(198, 161, 74, 0.3) 0%, transparent 70%)' }}
                    />
                    <div className="aspect-[4/5] overflow-hidden rounded-xl shadow-2xl">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_e3beda66-3777-486e-a151-323160967d26/artifacts/gtt8njnk_openart-image_raSLKBnH_1770853494368_raw.png"
                        alt="Full-body digital persona - commercial-ready influencer realism"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    {/* Caption */}
                    <p className="text-white/40 text-xs text-center mt-4 italic">
                      Full-body identity stability across environments and lighting conditions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <p className="text-center text-white/40 text-sm mt-12">
            Custom enterprise deployments and white-label builds available upon request.
          </p>
        </div>
      </section>

      {/* Value Justification Section */}
      <section className="py-16 md:py-20 bg-[#141c2b]">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              The Advantage
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              Why Commission a Private Model?
            </h2>
          </div>

          {/* Two-Column Layout */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            
            {/* Left Column: Image */}
            <div className="order-1 lg:order-1">
              <div className="relative">
                <div 
                  className="absolute -inset-4 rounded-3xl opacity-15 pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse at center, rgba(198, 161, 74, 0.3) 0%, transparent 70%)' }}
                />
                <div className="aspect-[4/5] overflow-hidden rounded-xl shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e3beda66-3777-486e-a151-323160967d26/artifacts/3wvheath_openart-image_6qvSHA1U_1770855584862_raw.png"
                    alt="Private AI model - consistent identity preservation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Text Content */}
            <div className="order-2 lg:order-2">
              <div className="text-white/60 space-y-6 leading-relaxed">
                <p>
                  Generating consistent, high-quality images of yourself shouldn't require hours of prompt engineering or endless GPU credits. Generic models produce generic results — and public services offer no privacy, no control, and no guarantee of quality.
                </p>
                <p className="text-white/80 font-medium text-lg">
                  A private LoRA changes everything.
                </p>
                <p>
                  You eliminate the guesswork. Every generation maintains your likeness with precision. You own the model outright — no subscriptions, no platform dependencies, no usage limits. Scale your content creation without scaling your costs.
                </p>
                <p className="text-white/70 italic">
                  This isn't a shortcut. It's infrastructure for creators who think long-term.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-16 md:py-20 bg-[#0f1724]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Ideal Clients
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Perfect For
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {perfectFor.map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-4 bg-[#141c2b] rounded-xl border border-white/5"
              >
                <div className="w-2 h-2 rounded-full bg-[#C6A14A]" />
                <span className="text-white/70">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-20 bg-[#141c2b]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Our Process
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              How It Works
            </h2>
          </div>
          
          <div className="space-y-6">
            {processSteps.map((step, idx) => (
              <div 
                key={idx}
                className="flex gap-6 p-6 bg-[#0f1724] rounded-xl border border-white/5"
              >
                <div className="flex-shrink-0">
                  <span className="text-[#C6A14A] text-2xl font-serif">{step.num}</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-white/50">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Not DIY Section */}
      <section className="py-16 md:py-20 bg-[#0f1724]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              The Reality
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              The Hidden Cost of "Doing It Yourself"
            </h2>
          </div>
          
          <div className="text-white/60 space-y-4 leading-relaxed mb-10">
            <p>
              Training a LoRA model looks simple on paper. Upload images, run the script, wait for results. But the reality is far more complex — and far more expensive than most creators anticipate.
            </p>
            <p>
              Without proper dataset engineering, trigger calibration, and output testing, you're not building a model. You're gambling with your time, your GPU budget, and your results.
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              { title: "Dataset inconsistency", desc: "Poor image selection leads to unpredictable outputs and identity confusion" },
              { title: "Identity drift", desc: "Undertrained or overtrained models lose resemblance after just a few generations" },
              { title: "Overfitting", desc: "The model memorizes instead of learning, producing rigid, unusable results" },
              { title: "GPU waste", desc: "Failed training runs burn through cloud credits with nothing to show" },
              { title: "Trigger misfires", desc: "Poorly calibrated activation words produce inconsistent or broken outputs" }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 p-4 bg-[#141c2b] rounded-xl border border-white/5">
                <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                <div>
                  <span className="text-white font-medium">{item.title}</span>
                  <span className="text-white/50"> — {item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 md:py-20 bg-[#141c2b]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              The Investment
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              The Return on Getting It Right
            </h2>
          </div>
          
          <div className="text-white/60 space-y-4 leading-relaxed mb-10">
            <p>
              A properly engineered private model isn't an expense — it's infrastructure. It eliminates the endless cycle of failed generations, wasted compute, and inconsistent results that drain both time and budget.
            </p>
            <p className="text-white/80">
              What you receive is a production-ready asset: consistent, scalable, and commercially viable from day one.
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              { title: "100+ hours saved", desc: "Skip the trial-and-error of dataset curation, training runs, and troubleshooting" },
              { title: "Immediate deployment", desc: "Generate production-quality content from your first prompt" },
              { title: "Unlimited scalability", desc: "One model, infinite outputs — no per-image costs, no platform fees" },
              { title: "Brand consistency", desc: "Every generation maintains your likeness with precision" },
              { title: "Commercial leverage", desc: "Monetize your content, license your likeness, or scale your brand presence" }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 p-4 bg-[#0f1724] rounded-xl border border-[#C6A14A]/10">
                <Check className="w-5 h-5 text-[#C6A14A] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-white font-medium">{item.title}</span>
                  <span className="text-white/50"> — {item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-[#0f1724]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
              Common Questions
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              Frequently Asked
            </h2>
          </div>
          
          <div className="space-y-4">
            {[
              {
                q: "What exactly is a private LoRA?",
                a: "A LoRA (Low-Rank Adaptation) is a lightweight AI model trained on your specific likeness. Unlike generic models, it learns your unique features and produces consistent, recognizable outputs on demand."
              },
              {
                q: "How long does the training process take?",
                a: "Most projects are delivered within 5–10 business days, depending on package tier and dataset complexity. Rush delivery is available for time-sensitive projects."
              },
              {
                q: "Do I own the final model?",
                a: "Yes. With the Private LoRA Build and Full Ownership packages, you receive the model files directly. The Full Ownership tier includes complete IP transfer and commercial usage rights."
              },
              {
                q: "What platforms can I use my model on?",
                a: "Your LoRA is compatible with any SDXL-based platform — including ComfyUI, Automatic1111, Forge, and cloud services like RunPod or Vast.ai. You control where and how it's used."
              },
              {
                q: "Can I use this commercially?",
                a: "The Starter tier is personal use only. The Private LoRA Build includes limited commercial rights. The Full Ownership Package includes unrestricted commercial usage and full IP transfer."
              },
              {
                q: "What makes this different from public models?",
                a: "Public models are trained on generic datasets and shared with thousands of users. Your private LoRA is trained exclusively on your likeness, never shared, and engineered for consistency — not just novelty."
              }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 bg-[#141c2b] rounded-xl border border-white/5">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-white/50 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#141c2b]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
            Ready to Begin?
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Commission Your Private Model
          </h2>
          <p className="text-white/50 text-lg leading-relaxed mb-8 max-w-xl mx-auto">
            Tell us about your project and we'll follow up within 24 hours with a custom recommendation tailored to your needs.
          </p>
          <Link 
            to="/order/lora-training"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#C6A14A] text-[#0f1724] font-semibold hover:bg-[#C6A14A]/90 transition-all shadow-lg"
            data-testid="cta-commission-btn"
          >
            Start Your Commission
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Bottom Spacer */}
      <div className="h-16 bg-gradient-to-b from-[#141c2b] to-[#050816]" />
    </div>
  );
};

export default DigitalPersonaDevelopment;
